import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const FilterBar = ({
  categories,
  selectedCategory,
  onCategoryChange,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  projectCount
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isMobileSortOpen, setIsMobileSortOpen] = useState(false);

  const sortOptions = [
    { value: 'recent', label: 'Most Recent' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'alphabetical', label: 'A-Z' },
    { value: 'client', label: 'Client Name' }
  ];

  const selectedSortLabel =
    sortOptions.find(option => option.value === sortBy)?.label || 'Sort';

  return (
    <div className="bg-surface border-b border-border sticky top-14 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        {/* Desktop Filter Bar */}
        <div className="hidden lg:flex items-center justify-between space-x-6">
          {/* Category Filters */}
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-text-secondary">Filter by:</span>
            <div className="flex items-center space-x-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => onCategoryChange(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-primary text-white'
                      : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  }`}
                >
                  {category.label}
                  <span className="ml-2 text-xs opacity-75">({category.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Search and Sort */}
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <Icon
                name="Search"
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
              />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10 pr-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm w-64"
              />
            </div>

            {/* Sort - Custom Dropdown */}
            <div className="relative w-40">
              <div
                onClick={() => setIsSortOpen(!isSortOpen)}
                className="flex items-center justify-between appearance-none bg-surface border border-border rounded-md px-4 py-2 pr-8 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200"
              >
                <span className="truncate whitespace-nowrap">{selectedSortLabel}</span>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 transform text-text-secondary transition-transform duration-200 ${
                    isSortOpen ? 'rotate-180' : ''
                  }`}
                />
              </div>

              {isSortOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-surface border border-border rounded-md shadow-lg overflow-hidden z-10">
                  {sortOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        onSortChange(option.value);
                        setIsSortOpen(false);
                      }}
                      className="px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 cursor-pointer truncate whitespace-nowrap"
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Filter Bar */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center space-x-2 px-4 py-2 bg-secondary-100 text-secondary-700 rounded-md"
              >
                <Icon name="Filter" size={16} />
                <span className="text-sm font-medium">Filters</span>
                <Icon name={isFilterOpen ? 'ChevronUp' : 'ChevronDown'} size={16} />
              </button>
              <span className="text-sm text-text-secondary">
                {projectCount} project{projectCount !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Mobile Sort - Custom Dropdown */}
            <div className="relative w-40">
              <div
                onClick={() => setIsMobileSortOpen(!isMobileSortOpen)}
                className="flex items-center justify-between appearance-none bg-surface border border-border rounded-md px-3 py-2 pr-8 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200"
              >
                <span className="truncate whitespace-nowrap">{selectedSortLabel}</span>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 transform text-text-secondary transition-transform duration-200 ${
                    isMobileSortOpen ? 'rotate-180' : ''
                  }`}
                />
              </div>

              {isMobileSortOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-surface border border-border rounded-md shadow-lg overflow-hidden z-10">
                  {sortOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        onSortChange(option.value);
                        setIsMobileSortOpen(false);
                      }}
                      className="px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 cursor-pointer truncate whitespace-nowrap"
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Search */}
          <div className="relative mb-4">
            <Icon
              name="Search"
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
            />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
          </div>

          {/* Mobile Category Filters */}
          <motion.div
            initial={false}
            animate={{ height: isFilterOpen ? 'auto' : 0 }}
            className="overflow-hidden"
          >
            <div className="pb-4">
              <div className="grid grid-cols-2 gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => onCategoryChange(category.id)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      selectedCategory === category.id
                        ? 'bg-primary text-white'
                        : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                    }`}
                  >
                    {category.label}
                    <span className="ml-1 text-xs opacity-75">({category.count})</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Results Count */}
        <div className="hidden lg:flex items-center justify-between mt-2 pt-2 border-t border-border">
          <span className="text-sm text-text-secondary">
            Showing {projectCount} project{projectCount !== 1 ? 's' : ''}
            {selectedCategory !== 'All' && ` in ${selectedCategory}`}
            {searchQuery && ` matching "${searchQuery}"`}
          </span>

          {/* Active Filters */}
          {(selectedCategory !== 'All' || searchQuery) && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-text-secondary">Active filters:</span>
              {selectedCategory !== 'All' && (
                <span className="bg-primary-50 text-primary px-2 py-1 rounded text-xs font-medium flex items-center space-x-1">
                  <span>{selectedCategory}</span>
                  <button
                    onClick={() => onCategoryChange('All')}
                    className="hover:text-primary-700"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="bg-primary-50 text-primary px-2 py-1 rounded text-xs font-medium flex items-center space-x-1">
                  <span>"{searchQuery}"</span>
                  <button
                    onClick={() => onSearchChange('')}
                    className="hover:text-primary-700"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
